import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import CircularProgress from '@mui/material/CircularProgress'
import _ from 'lodash'
import { useTeam } from '../../../contexts/TeamContext'
import { useRegistryCredentials } from '../../../contexts/RegistryCredentialsContext'
import { useRegistries } from '../../../contexts/RegistriesContext'
import useRegistryCredentialsResources from '../../../shared/resources/api/v1/admin/docker/useRegistryCredentialsResources'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import Page from '../../../shared/components/layouts/Page'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import FormDrawer from '../../../shared/components/drawers/FormDrawer'
import RegistryForm from './components/RegistryForm'

const getStatusColor = (status) => {
  switch (status) {
    case 'valid':
      return 'success'
    case 'invalid':
      return 'error'
    default:
      return 'warning'
  }
}

function RegistryIndexPage() {
  const { teamSlug } = useParams()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { team } = useTeam()
  const { registryCredentials, loading, error, reload } = useRegistryCredentials()
  const { registriesById } = useRegistries()
  const { create, update, destroy, verify } = useRegistryCredentialsResources(teamSlug)
  const { openConfirmationDialog } = useConfirmationDialog()

  const [drawer, setDrawer] = useState({ open: false, registryCredential: null })
  const [verifyingIds, setVerifyingIds] = useState({})

  const handleOpenDrawer = (registryCredential = null) => {
    setDrawer({ open: true, registryCredential })
  }

  const handleCloseDrawer = () => {
    setDrawer({ open: false, registryCredential: null })
  }

  const handleFormSuccess = (response) => {
    const status = _.get(response, 'registryCredential.validationStatus')
    if (status === 'valid') {
      enqueueSnackbar(formatMessage({ id: 'registries.form.success' }), { variant: 'success' })
    } else {
      enqueueSnackbar(formatMessage({ id: 'registries.form.invalid' }), { variant: 'warning' })
    }
    handleCloseDrawer()
    reload()
  }

  const handleDelete = useCallback((registryCredential) => {
    openConfirmationDialog({
      title: <FormattedMessage id="registries.delete.title" />,
      message: <FormattedMessage id="registries.delete.message" />,
      onConfirm: () => destroy(registryCredential.id)
        .then(() => {
          reload()
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'registries.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, reload, enqueueSnackbar, formatMessage])

  const handleVerify = useCallback((registryCredential) => {
    setVerifyingIds(prev => ({ ...prev, [registryCredential.id]: true }))
    verify(registryCredential.id)
      .then((response) => {
        const status = _.get(response, 'registryCredential.validationStatus')
        if (status === 'valid') {
          enqueueSnackbar(formatMessage({ id: 'registries.verify.success' }), { variant: 'success' })
        } else {
          enqueueSnackbar(formatMessage({ id: 'registries.verify.invalid' }), { variant: 'error' })
        }
        reload()
      })
      .catch(() => {
        enqueueSnackbar(formatMessage({ id: 'registries.verify.error' }), { variant: 'error' })
      })
      .finally(() => {
        setVerifyingIds(prev => ({ ...prev, [registryCredential.id]: false }))
      })
  }, [verify, reload, enqueueSnackbar, formatMessage])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="registries.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="registries.title" />}
        entities={{ team }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
          >
            <FormattedMessage id="registries.actions.create" />
          </Button>
        }
      />
      <PageBody>
        {registryCredentials.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="registries.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="registries.index.columns.registry" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="registries.index.columns.username" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="registries.index.columns.status" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="registries.index.columns.createdAt" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="registries.index.columns.actions" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registryCredentials.map((registryCredential) => {
                  const registry = _.get(registriesById, registryCredential.registryId, {})
                  const registryUrl = _.get(registry, 'url', 'Unknown')
                  const statusChip = (
                    <Chip
                      label={<FormattedMessage id={`registries.status.${registryCredential.validationStatus}`} />}
                      color={getStatusColor(registryCredential.validationStatus)}
                      size="small"
                    />
                  )

                  return (
                    <TableRow key={registryCredential.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {registryUrl}
                        </Typography>
                      </TableCell>
                      <TableCell>{registryCredential.username}</TableCell>
                      <TableCell>
                        {registryCredential.validationError ? (
                          <Tooltip title={registryCredential.validationError}>
                            {statusChip}
                          </Tooltip>
                        ) : (
                          statusChip
                        )}
                      </TableCell>
                      <TableCell>
                        <FormattedDate
                          value={registryCredential.createdAt}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={<FormattedMessage id="registries.actions.verify" />}>
                          <IconButton
                            size="small"
                            onClick={() => handleVerify(registryCredential)}
                            disabled={verifyingIds[registryCredential.id]}
                          >
                            {verifyingIds[registryCredential.id] ? (
                              <CircularProgress size={18} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDrawer(registryCredential)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(registryCredential)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageBody>

      <FormDrawer
        open={drawer.open}
        onClose={handleCloseDrawer}
        titleId={drawer.registryCredential ? 'registries.edit.title' : 'registries.create.title'}
      >
        <RegistryForm
          registryCredential={drawer.registryCredential}
          createRegistryCredential={create}
          updateRegistryCredential={update}
          onSuccess={handleFormSuccess}
          onCancel={handleCloseDrawer}
        />
      </FormDrawer>
    </Page>
  )
}

export default RegistryIndexPage
